/**
 * @deprecated Use `StyledSelect` from `@/shared/components/ui/styled-select` instead.
 *
 * This component renders a native `<select>` element. New code should use
 * `StyledSelect` which is built on Radix UI and matches the design system.
 *
 * Migration notes:
 * - `StyledSelect` uses `value` (string) + `onValueChange` instead of
 *   native `onChange` / `e.target.value`.
 * - Radix Select does not support empty-string values. For "All" / placeholder
 *   options that previously used `value=""`, use a sentinel like `'__all__'`
 *   and map it to `null` in `onValueChange`.
 * - The `label` prop is not supported on `StyledSelect`; render your own
 *   `<label>` alongside it if needed.
 */

import { forwardRef, type SelectHTMLAttributes } from 'react'
import { cn } from '@/shared/utils/cn'

export interface SelectOption {
  value: string
  label: string
}

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'children'> {
  label?: string
  options: SelectOption[]
  placeholder?: string
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  ({ label, options, placeholder, className, id, ...props }, ref) => {
    const selectId = id || label?.toLowerCase().replace(/\s+/g, '-')

    return (
      <div className="form-group">
        {label && (
          <label htmlFor={selectId} className="form-label">
            {label}
          </label>
        )}
        <select
          ref={ref}
          id={selectId}
          className={cn('filter-select', className)}
          {...props}
        >
          {placeholder && <option value="">{placeholder}</option>}
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>
    )
  }
)

Select.displayName = 'Select'
